import { isEqual, isUndefined, omit, omitBy } from 'lodash-es'
import { injectable } from '~/container'
import { Logger } from '../../../composables/useGtmLogRequest'
import { MapSearchContext, SearchFilters } from '../schemas'
import { MapSearchEngineState } from '../types'
import { MapSearchState, getMapSearchMachineInterpreted } from './machine'
import { logger } from '../../../utility/logger'

@injectable()
export class MapSearchEngine {
  abortController?: AbortController
  searchFilters?: SearchFilters

  constructor(
    public machine: ReturnType<typeof getMapSearchMachineInterpreted>,
  ) {}

  start() {
    this.machine.start()
  }

  stop() {
    this.abort()
    this.machine.stop()
  }

  search(filters: Partial<SearchFilters>) {
    const searchFilters = this.getDefaultFilters(filters)
    const onlyBoundsChanged = this.onlyBoundsChanged(
      this.searchFilters,
      searchFilters,
    )
    if (!isEqual(searchFilters, this.searchFilters)) {
      const type = this.getSearchType(searchFilters)
      if (this.abortController) {
        this.abortController.abort()
      }
      this.abortController = new AbortController()
      this.machine.send({
        type,
        filters: searchFilters,
        signal: this.abortController.signal,
        onlyBoundsChanged,
      })
      this.searchFilters = searchFilters
    }
  }

  abort() {
    logger('MapSearchEngine.abort')
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = undefined
  }

  // Update the getDefaultFilters method to remove undefined filter parameters
  private getDefaultFilters(filters: SearchFilters) {
    return omitBy(
      {
        ...filters,
        categoryIds: filters.categoryIds || [],
        facets: filters.facets || [],
      },
      isUndefined,
    )
  }

  // checks if only location props have changed, so we can keep markers
  private onlyBoundsChanged(
    oldFilters: SearchFilters | undefined,
    newFilters: SearchFilters,
  ): boolean {
    if (!oldFilters || !newFilters) {
      return false
    }
    const excludedProps = ['bounds', 'hierarchyPath', 'area']
    const oldRest = omit(oldFilters, excludedProps)
    const newRest = omit(newFilters, excludedProps)
    return isEqual(oldRest, newRest)
  }

  private getSearchType(filters: SearchFilters) {
    if (filters.dates) {
      return 'priceSearch'
    }
    return 'search'
  }

  get status() {
    return this.machine.state.toStrings()[0]
  }

  onContext(listener: (context: MapSearchContext) => void) {
    this.machine.onChange((context) => {
      listener(context)
    })
  }

  onState(listener: (state: MapSearchEngineState) => void) {
    this.machine.onTransition((state) => {
      const stateName = this.getCurrentStateName(
        state as unknown as MapSearchState,
      )
      listener(stateName)
    })
  }

  private getCurrentStateName(state: MapSearchState): MapSearchEngineState {
    if (state.matches('searching')) {
      return 'searching'
    }
    if (state.matches('results')) {
      return 'results'
    }
    if (state.matches('error')) {
      return 'error'
    }
    return 'idle'
  }
}

export function getMapSearchEngine(langCode: string, logger: Logger) {
  const machine = getMapSearchMachineInterpreted(langCode, logger)
  return new MapSearchEngine(machine)
}
